import Button from 'components/Form/Button';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BankType } from '../types/on-boarding.type';
import { TypeOfBankAccount } from 'constants/bank-account-type';
import CustomSelect from 'components/Form/CustomSelect';
import {
  useCreateBank,
  useDeleteBank,
  useGetBankList,
  useGetNigeriaBanks,
  useVerifyBVN,
  useVerifyBankAccount
} from 'common/queries-and-mutations/bank';
import { Loader } from 'components/Loader';
import {
  findNextPage,
  findPreviousPage,
  isValidNumber,
  showRemoveButton
} from 'utils';
import { debounce } from 'lodash';
import { countriesCurrency } from 'constants/countries-currency';
import InfoBar from './InfoBar';
import { STEPS } from 'constants/steps';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import { fetchUserInformation } from 'thunks/account-thunk';
import { useSkipOnboardingStep } from 'common/queries-and-mutations/user';

export default function BankInfo() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchAccountNumber, setSearchAccountNumber] = useState('');
  const [searchBVN, setSearchBVN] = useState('');
  const [isLater, setIsLater] = useState(false);

  const { mutate, isSuccess, isError, isLoading } = useCreateBank();
  const { mutate: deleteBank, isLoading: isDeleting } = useDeleteBank();
  const { mutate: skipOnboarding, isSuccess: skipSuccess } =
    useSkipOnboardingStep();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAccountDetails);
  const planAllowPage = user && JSON.parse(user?.planAllowPage);

  const parts = window.location.pathname.split('/');
  const parentPath = parts[1];

  const {
    mutate: verifyAccount,
    isSuccess: isVerified,
    isError: isVerifiedError,
    isLoading: isFetching,
    data: verifyData
  } = useVerifyBankAccount();
  const {
    mutate: verifyBVN,
    isSuccess: isBVNVerified,
    isLoading: isBVNFetching,
    data: bvnData
  } = useVerifyBVN();

  const {
    data,
    isSuccess: isBankSuccess,
    isLoading: isBankLoading
  } = useGetBankList();

  const { data: bankList } = useGetNigeriaBanks();

  const computeBankList = useMemo(() => {
    return bankList?.map(bank => ({
      value: bank.code,
      label: bank.name
    }));
  }, [bankList]);

  const banks = useMemo(() => {
    if (isBankSuccess && data.length > 0) {
      return data.map(bank => ({
        bankId: bank.id,
        accountName: bank.accountName,
        accountNumber: bank.accountNumber,
        bvn: bank.bvn,
        accountType: bank.accountType,
        bankName: bank.bankName,
        bankCode: bank.bankCode,
        bankCountry: bank.bankCountry,
        branch: bank.branch
      }));
    } else {
      return [
        {
          bankId: null,
          accountName: '',
          accountNumber: '',
          bvn: '',
          accountType: '',
          bankName: '',
          bankCode: '',
          bankCountry: '',
          branch: ''
        }
      ];
    }
  }, [data, isBankSuccess]);

  const formik = useFormik({
    initialValues: {
      banks
    },
    validationSchema: Yup.object({
      banks: Yup.array().of(
        Yup.object().shape({
          bankCountry: Yup.string()
            .required('Country of the bank is required')
            .nullable(),
          bankName: Yup.string().required('Please select bank'),
          accountNumber: Yup.string()
            .required('Account number is required')
            .nullable(),

          // accountNumber: Yup.string().when('bankCountry', {
          //   is: 'Nigeria',
          //   then: Yup.string()
          //     .max(10, 'Invalid account number provided')
          //     .required('Account number is required')
          //     .nullable(),
          //   otherwise: Yup.string()
          //     .required('Account number is required')
          //     .nullable()
          // }),

          accountName: Yup.string()
            .required('Account name is required')
            .nullable(),
          accountType: Yup.string().when('bankCountry', {
            is: 'Nigeria',
            then: Yup.string().required('Account Type is required').nullable(),
            otherwise: Yup.string()
          })
          // bvn: Yup.string()
          //   .required('Postcode is required').nullable()
          //   .test(
          //     'is-allowed',
          //     'Please provide a valid bvn',
          //     async function (value) {
          //       if (value) {
          //         const isValid = await services.bankService.verifyBVN(value);
          //         return isValid;
          //       }
          //       return false;
          //     }
          //   )
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBankSubmit(values);
    }
  });

  const addMoreBank = () => {
    formik.values.banks.push({
      bankId: null,
      accountName: '',
      accountNumber: '',
      bvn: '',
      accountType: '',
      bankName: '',
      bankCode: '',
      bankCountry: '',
      branch: ''
    });
    formik.setFieldValue('banks', [...formik.values.banks]);
  };

  const removeBank = (indexToRemove: number, bankId?: number | null) => {
    if (bankId) {
      deleteBank(bankId);
    }
    formik.values.banks.splice(indexToRemove, 1);
    formik.setFieldValue('banks', [...formik.values.banks]);
  };

  const handleSearchAndUpdateForAccount = useCallback(() => {
    const { banks } = formik.values;
    const bankIndex = banks.findIndex(
      bank => bank.accountNumber === searchAccountNumber
    );
    if (bankIndex !== -1) {
      if (verifyData) {
        formik.setFieldValue(
          `banks[${bankIndex}].accountName`,
          verifyData?.account_name.toUpperCase()
        );
      } else {
        formik.setFieldValue(`banks[${bankIndex}].accountName`, '');
      }
    }
  }, [formik, verifyData]);

  useEffect(() => {
    if (isVerified && !isFetching) {
      handleSearchAndUpdateForAccount();
    }
    if (isVerifiedError) {
      const { banks } = formik.values;
      const bankIndex = banks.findIndex(
        bank => bank.accountNumber === searchAccountNumber
      );
      if (bankIndex !== -1) {
        formik.setFieldValue(`banks[${bankIndex}].accountName`, '');
      }
    }
  }, [isFetching, isVerified]);

  const executeSearch = useCallback(
    debounce((bankCode: string, accountNumber: string) => {
      verifyAccount({ bankCode, accountNo: accountNumber });
    }, 2000),
    []
  );

  const executeBVNSearch = useCallback(
    debounce((bvn: string) => {
      verifyBVN(bvn);
    }, 2000),
    []
  );

  const handleChange = (bankCode: string, accountNumber: string) => {
    if (accountNumber) {
      setSearchAccountNumber(accountNumber.toString());
      executeSearch(bankCode, accountNumber);
    }
  };

  const handleBVNChange = (bvn: string) => {
    if (bvn) {
      setSearchBVN(bvn.toString());
      executeBVNSearch(bvn);
    }
  };

  const onBankSelect = (
    index: number,
    option: {
      label: string;
      value: string | number;
    } | null
  ) => {
    formik.setFieldValue(`banks[${index}].bankName`, option?.label);
    formik.setFieldValue(`banks[${index}].bankCode`, option?.value);
    const bank = bankList?.find(bank => bank.code === option?.value);
    formik.setFieldValue(`banks[${index}].bankCountry`, bank?.country);
    formik.setFieldValue(`banks[${index}].accountNumber`, '');
    formik.setFieldValue(`banks[${index}].accountName`, '');
  };

  const handleSearchAndUpdateForBVN = useCallback(() => {
    const { banks } = formik.values;
    const bankIndex = banks.findIndex(bank => bank.bvn === searchBVN);
    if (bankIndex !== -1) {
      if (bvnData) {
        formik.setFieldError(`banks[${bankIndex}].bvn`, '');
      } else {
        formik.setFieldError(`banks[${bankIndex}].bvn`, 'Provide valid bvn');
      }
    }
  }, [formik, searchBVN, bvnData]);

  useEffect(() => {
    if (isBVNVerified && !isBVNFetching) {
      handleSearchAndUpdateForBVN();
    }
  }, [isFetching, isVerified]);

  const onBankSubmit = (formValue: BankType) => {
    mutate(formValue);
  };

  const onContinueLater = () => {
    if (Object.keys(formik.errors).length) {
      return;
    }
    mutate(formik.values);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      dispatch(fetchUserInformation());
      if (isLater) {
        navigate('/dashboard');
        setIsLater(false);
        return;
      }
      navigate(findNextPage(planAllowPage, STEPS.BANK_INFORMATION));
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError, isLater, dispatch, navigate, planAllowPage]);

  const onPrevious = () => {
    navigate(findPreviousPage(planAllowPage, STEPS.BANK_INFORMATION));
  };

  const handleAccountNumberChange = (
    index: number,
    bankCountry: string,
    bankCode: any,
    e: ChangeEvent<any>
  ) => {
    const accountNumber = e.target.value;

    if (isValidNumber(accountNumber) || accountNumber === '') {
      if (bankCountry === 'Nigeria' && accountNumber.length <= 10) {
        formik.handleChange(e);
        handleChange(bankCode, accountNumber);
      } else if (bankCountry === 'Nigeria' && accountNumber.length >= 11) {
        formik.setFieldError(
          `banks[${index}].accountNumber`,
          'Invalid account number provided'
        );
      } else if (bankCountry !== 'Nigeria') {
        formik.handleChange(e);
      }
    }
  };

  const handleSkip = () => {
    setIsSubmitting(true);
    skipOnboarding();
  };

  useEffect(() => {
    if (skipSuccess) {
      dispatch(fetchUserInformation());
      navigate('/dashboard');
    }
  }, [skipSuccess, dispatch]);

  return (
    <div className="mx-10">
      <InfoBar
        stepCounter={2}
        stepPercentage={STEPS.BANK_INFORMATION}
        timelineStep={2}
      />

      {isBankLoading ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          {(isFetching || isBVNFetching) && (
            <Loader color={'#FF6606'} height={50} width={50} />
          )}

          <div className="flex w-full items-end justify-between">
            <span></span>
            <p className="my-9 text-center font-circular text-lg font-medium text-[#353535]">
              Financial Asset - Bank Information
            </p>
            <button
              type="button"
              className="mt-5 text-sm font-normal text-[#353535] underline"
              onClick={handleSkip}
            >
              Skip to Dashboard
            </button>
          </div>
          <form
            autoComplete="false"
            className="m-10"
            onSubmit={formik.handleSubmit}
          >
            {formik.values.banks.map((bank, index) => (
              <div key={index} className="my-8">
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 w-full">
                    <CustomSelect
                      options={countriesCurrency.map(country => ({
                        value: country.name,
                        label: country.name
                      }))}
                      selectedOption={{
                        value: bank.bankCountry,
                        label: bank.bankCountry
                      }}
                      handleOptionChange={option => {
                        formik.resetForm();
                        formik.setFieldValue(
                          `banks[${index}].bankCountry`,
                          option?.value
                        );
                      }}
                      label="Country of the Bank Account"
                      name="bankCountry"
                      isRequired
                      isSearchable
                      className="h-[3rem] rounded-2xl"
                      error={
                        formik.touched.banks
                          ? formik.errors.banks?.[index] &&
                            formik.errors.banks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.banks[index])
                              ).bankCountry
                            : ''
                          : ''
                      }
                    />
                  </div>
                  {bank.bankCountry === 'Nigeria' ? (
                    <div className="group relative z-0 mb-6 w-full">
                      <CustomSelect
                        options={computeBankList}
                        selectedOption={{
                          value: bank.bankCode,
                          label: bank.bankName
                        }}
                        handleOptionChange={option =>
                          onBankSelect(index, option)
                        }
                        label="Select Bank"
                        name="bank"
                        isRequired
                        isSearchable
                        className="h-[3rem] rounded-2xl"
                        error={
                          formik.touched.banks
                            ? formik.errors.banks?.[index] &&
                              formik.errors.banks.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.banks[index])
                                ).bankName
                              : ''
                            : ''
                        }
                      />
                    </div>
                  ) : (
                    <div className="group relative z-0 mb-6 w-full">
                      <TextField
                        name={`banks[${index}].bankName`}
                        placeholder=""
                        value={bank.bankName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label="Bank Name"
                        error={
                          formik.touched.banks
                            ? formik.errors.banks?.[index] &&
                              formik.errors.banks.length > 0
                              ? JSON.parse(
                                  JSON.stringify(formik.errors.banks[index])
                                ).bankName
                              : ''
                            : ''
                        }
                        isRequired
                        size="lg"
                        className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                      />
                    </div>
                  )}
                </div>
                <div className="grid md:grid-cols-2 md:gap-6">
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`banks[${index}].accountNumber`}
                      placeholder=""
                      value={bank.accountNumber}
                      onChange={e =>
                        handleAccountNumberChange(
                          index,
                          bank.bankCountry,
                          bank.bankCode,
                          e
                        )
                      }
                      onBlur={e =>
                        handleAccountNumberChange(
                          index,
                          bank.bankCountry,
                          bank.bankCode,
                          e
                        )
                      }
                      label="Account Number"
                      error={
                        formik.touched.banks
                          ? formik.errors.banks?.[index] &&
                            formik.errors.banks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.banks[index])
                              ).accountNumber
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                      isLoading={isFetching}
                    />
                  </div>
                  <div className="group relative z-0 mb-6 w-full">
                    <TextField
                      name={`banks[${index}].accountName`}
                      placeholder=""
                      value={bank.accountName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Account Name"
                      disabled={bank.bankCountry === 'Nigeria'}
                      error={
                        formik.touched.banks
                          ? formik.errors.banks?.[index] &&
                            formik.errors.banks.length > 0
                            ? JSON.parse(
                                JSON.stringify(formik.errors.banks[index])
                              ).accountName
                            : ''
                          : ''
                      }
                      isRequired
                      size="lg"
                      className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                    />
                  </div>
                </div>
                {bank.bankCountry === 'Nigeria' && (
                  <>
                    <div className="grid md:grid-cols-2 md:gap-6">
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`banks[${index}].bvn`}
                          placeholder=""
                          value={bank.bvn}
                          onChange={e => {
                            if (
                              isValidNumber(e.target.value) ||
                              e.target.value === ''
                            ) {
                              formik.handleChange(e);
                              if (e.target.value.length === 11) {
                                handleBVNChange(e.target.value);
                              }
                            }
                          }}
                          onBlur={e => {
                            if (
                              isValidNumber(e.target.value) ||
                              e.target.value === ''
                            ) {
                              formik.handleChange(e);
                              if (e.target.value.length === 11) {
                                handleBVNChange(e.target.value);
                              }
                            }
                          }}
                          label="BVN"
                          error={
                            formik.touched.banks
                              ? formik.errors.banks?.[index] &&
                                formik.errors.banks.length > 0
                                ? JSON.parse(
                                    JSON.stringify(formik.errors.banks[index])
                                  ).bvn
                                : ''
                              : ''
                          }
                          size="lg"
                          className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                          isLoading={isBVNFetching}
                          isRequired
                        />
                      </div>
                      <div className="group relative z-0 mb-6 w-full">
                        <CustomSelect
                          options={TypeOfBankAccount}
                          selectedOption={{
                            value: bank.accountType,
                            label: bank.accountType
                          }}
                          handleOptionChange={option => {
                            formik.setFieldValue(
                              `banks[${index}].accountType`,
                              option?.value
                            );
                          }}
                          label="Account Type"
                          name="bank"
                          isRequired
                          isSearchable
                          className="h-[3rem] rounded-2xl"
                          error={
                            formik.touched.banks
                              ? formik.errors.banks?.[index] &&
                                formik.errors.banks.length > 0
                                ? JSON.parse(
                                    JSON.stringify(formik.errors.banks[index])
                                  ).accountType
                                : ''
                              : ''
                          }
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 md:gap-6">
                      <div className="group relative z-0 mb-6 w-full">
                        <TextField
                          name={`banks[${index}].branch`}
                          placeholder=""
                          value={bank.branch}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Bank Branch"
                          error={
                            formik.touched.banks
                              ? formik.errors.banks?.[index] &&
                                formik.errors.banks.length > 0
                                ? JSON.parse(
                                    JSON.stringify(formik.errors.banks[index])
                                  ).branch
                                : ''
                              : ''
                          }
                          size="lg"
                          className="h-[3rem] rounded-2xl border-[1.5px] border-gray-300"
                        />
                      </div>
                    </div>
                  </>
                )}

                {showRemoveButton(index, bank.bankId, data!.length > 1) && (
                  <div className="group relative z-0 mt-1 w-full">
                    {isDeleting ? (
                      <Loader height={20} width={20} type={'Wait'} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeBank(index, bank.bankId)}
                      >
                        <Icon name={Icons.Delete} />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            <div className="mt-5 flex items-center justify-between gap-2">
              <div className="flex w-full items-center gap-2">
                <Icon name={Icons.Required} />
                <span className="text-[10px] text-[#7A7C87]">
                  Indicates Required
                </span>
              </div>
              <div className="group z-0 flex w-full items-center justify-end">
                <div
                  className="flex items-center gap-4"
                  onClick={addMoreBank}
                  role="button"
                  tabIndex={-1}
                >
                  <Icon name={Icons.AddCircleOutline} />
                  <span className="mt-2 text-base font-medium text-[#353535]">
                    Add Another Account
                  </span>
                </div>
              </div>
            </div>

            <div className="flex w-full items-end justify-between">
              <Button
                variant="outline"
                size="md"
                className="mt-5 w-[9rem] rounded p-[0.1rem] text-primary-900"
                label="Previous"
                type="button"
                onClick={() => onPrevious()}
              ></Button>
              <div className="mt-10 flex items-end justify-end gap-8">
                {parentPath !== 'dashboard' && (
                  <button
                    type="submit"
                    className="font-circular text-[15px] font-bold text-[#353535] underline"
                    onClick={() => {
                      onContinueLater();
                      setIsLater(true);
                    }}
                  >
                    Save and Continue Later
                  </button>
                )}
                <Button
                  variant="primary"
                  size="custom"
                  className="mt-5 rounded p-3"
                  label="Add New Account"
                  type="submit"
                  disabled={!formik.isValid}
                  loading={isSubmitting || isLoading}
                ></Button>
              </div>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
